import * as moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  RSelect,
} from "../../../components/Component";
import DatePicker from "../../../components/range-date-picker/RangeDatePicker";
import Table from "../../../components/react-table/Table";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { GET } from "../../../helper/api";
import { useUser } from "../../../context/user";
import { BILL_TYPE, USER_ROLES } from "../../../utils/Utils";
import CreatePNRequestBillModal from "./CreatePNRequestBillModal";

const ALLOWED_ROLES = [USER_ROLES.ENTRY_STAFF, USER_ROLES.MANAGER, USER_ROLES.SHIFT_LEADER, USER_ROLES.ADMIN];

const SHIFT_OPTIONS = [
  { label: "Sáng", value: "SÁNG" },
  { label: "Trưa", value: "TRƯA" },
  { label: "Chiều", value: "CHIỀU" },
  { label: "Cả ngày", value: "CẢ NGÀY" },
];

const SHIFT_LABEL = {
  "CẢ NGÀY": "Cả ngày",
  SÁNG: "Sáng",
  TRƯA: "Trưa",
  CHIỀU: "Chiều",
};

const STATUS_CONFIG = {
  rejected:  { label: "Từ chối",    color: "#dc3545" },
  completed: { label: "Hoàn thành", color: "#0d6efd" },
  approved:  { label: "Đã duyệt",   color: "#198754" },
  pending:   { label: "Chờ duyệt",  color: "#ffc107" },
};

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({ label, value }));

const getBillStatus = (bill) => {
  if (bill.rejectReason) return "rejected";
  if (bill.metadata?.completedAt) return "completed";
  if (bill.isAccept === true) return "approved";
  return "pending";
};

const StatusBadge = ({ status }) => {
  const { label, color } = STATUS_CONFIG[status];
  const isPending = status === "pending";
  return (
    <span style={
      isPending
        ? { border: `1px solid ${color}`, color, borderRadius: 4, padding: "2px 8px", fontSize: 11, whiteSpace: "nowrap", fontWeight: 600 }
        : { backgroundColor: color, color: "#fff", borderRadius: 4, padding: "2px 8px", fontSize: 11, whiteSpace: "nowrap", fontWeight: 600 }
    }>
      {label}
    </span>
  );
};

const BillCard = ({ bill, locationNameMap, onEdit }) => {
  const status = getBillStatus(bill);
  const fromName = locationNameMap[bill.fromLocation] || bill.fromLocation || "—";
  const toName = locationNameMap[bill.toLocation] || bill.toLocation || "—";
  const tripCount = Array.isArray(bill.trips) ? bill.trips.length : 0;
  const lastTap = useRef(0);

  // Double tap để vào màn hình sửa
  const handleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      lastTap.current = 0;
      onEdit(bill.id);
    } else {
      lastTap.current = now;
    }
  };

  const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <div
      onClick={handleTap}
      style={{
        background: "#fff",
        borderRadius: 8,
        border: "1px solid #e5e9f2",
        padding: "7px 10px",
        marginBottom: 6,
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Row 1: Hàng hoá + Loại + Trạng thái */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "#364a63", ...ellipsis }}>
            {bill.ingredientVATName || "—"}
          </span>
          {bill.customerDirection && (
            <span style={{
              fontSize: 9.5,
              color: bill.customerDirection === "IN" ? "#0d6efd" : "#e85347",
              border: `1px solid ${bill.customerDirection === "IN" ? "#0d6efd" : "#e85347"}`,
              borderRadius: 3,
              padding: "0 4px",
              flexShrink: 0,
            }}>
              {BILL_TYPE[bill.customerDirection] || bill.customerDirection}
            </span>
          )}
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Row 2: Tuyến đường + Buổi */}
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3, color: "#526484", fontSize: 11 }}>
        <span style={{ fontWeight: 500, ...ellipsis }}>{fromName}</span>
        <span style={{ color: "#b7c2d0", flexShrink: 0 }}>→</span>
        <span style={{ fontWeight: 500, ...ellipsis }}>{toName}</span>
        {bill.startShift && (
          <span style={{
            marginLeft: "auto",
            flexShrink: 0,
            background: "#f0f2f5",
            color: "#526484",
            borderRadius: 3,
            padding: "0 5px",
            fontSize: 10,
          }}>
            {SHIFT_LABEL[bill.startShift] || bill.startShift}
          </span>
        )}
      </div>

      {/* Row 3: KL + Số chuyến */}
      <div style={{ display: "flex", gap: 14, marginTop: 3, fontSize: 11, color: "#8094ae" }}>
        <span>KL: <strong style={{ color: "#364a63" }}>{bill.weight ? bill.weight.toLocaleString("en-US") : "—"} kg</strong></span>
        <span>Chuyến: <strong style={{ color: "#364a63" }}>{tripCount}</strong></span>
      </div>

      {/* Lý do từ chối */}
      {bill.rejectReason && (
        <div style={{ marginTop: 3, fontSize: 10.5, color: "#dc3545", ...ellipsis }}>
          <strong>Từ chối:</strong> {bill.rejectReason}
        </div>
      )}
    </div>
  );
};

const PNRequestBillManager = () => {
  const history = useHistory();
  const location = useLocation();
  const clearTimer = useRef();
  const {
    state: { user: currentUser },
  } = useUser();

  const canAccess = ALLOWED_ROLES.includes(currentUser?.role);

  const searchParams = queryString.parse(location.search);

  const [smOption, setSmOption] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [timeNow, setTimeNow] = useState();
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [rangeDate, setRangeDate] = useState({
    fromDate: moment(searchParams?.fromDate || undefined).startOf("day").toDate(),
    toDate: moment(searchParams?.toDate || undefined).endOf("day").toDate(),
  });
  const [filter, setFilter] = useState({
    ingredientVATName: searchParams?.ingredientVATName || "",
    startShift: searchParams?.startShift || "",
    userId: searchParams?.userId || "",
    status: searchParams?.status
      ? (Array.isArray(searchParams.status) ? searchParams.status : [searchParams.status])
      : ["pending", "approved"],
  });

  const [pnCustomers, setPnCustomers] = useState([]);
  const [locationDistances, setLocationDistances] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    if (!canAccess) {
      history.replace("/");
      return;
    }
    GET("/pn-customer", {}, true).then(({ data }) => setPnCustomers(data?.rows || data || []));
    GET("/location-distance", {}, true).then(({ data }) => setLocationDistances(data || []));
    GET("/user", {}, true).then(({ data }) => {
      const rows = data?.rows || data || [];
      setUserOptions(rows.map((u) => ({ label: u.name, value: String(u.id) })));
    });
  }, []);

  useEffect(() => {
    if (!canAccess) return;
    const newParams = queryString.stringify({
      fromDate: moment(rangeDate.fromDate).format("YYYY-MM-DD"),
      toDate: moment(rangeDate.toDate).format("YYYY-MM-DD"),
      ...(filter.ingredientVATName ? { ingredientVATName: filter.ingredientVATName } : {}),
      ...(filter.startShift ? { startShift: filter.startShift } : {}),
      ...(filter.userId ? { userId: filter.userId } : {}),
      ...(filter.status?.length ? { status: filter.status } : {}),
    });
    history.replace({ pathname: location.pathname, search: newParams });
    loadData();
  }, [timeNow, filter]);

  const loadData = () => {
    setLoading(true);
    clearTimeout(clearTimer.current);
    clearTimer.current = setTimeout(() => {
      GET(
        "/pn/pn-request-bill",
        {
          startDate: moment(rangeDate.fromDate).format("YYYY-MM-DD"),
          endDate: moment(rangeDate.toDate).format("YYYY-MM-DD"),
        },
        true
      )
        .then(({ data }) => setAllData(data?.rows || []))
        .catch(() => setAllData([]))
        .finally(() => setLoading(false));
    }, 300);
  };

  const onRangeChange = (dates) => {
    const [fromDate, toDate] = dates;
    setRangeDate({
      fromDate: fromDate ? moment(fromDate).startOf("day").toDate() : fromDate,
      toDate: toDate ? moment(toDate).endOf("day").toDate() : toDate,
    });
    if (fromDate && toDate) setTimeNow(moment().valueOf());
  };

  const locationNameMap = useMemo(() => {
    const map = {};
    locationDistances.forEach((d) => {
      if (d.fromLocation) map[d.fromLocation] = d.fromLocationName;
      if (d.toLocation) map[d.toLocation] = d.toLocationName;
    });
    return map;
  }, [locationDistances]);

  const customerMap = useMemo(() => {
    const map = {};
    pnCustomers.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [pnCustomers]);

  const userMap = useMemo(() => {
    const map = {};
    userOptions.forEach((u) => { map[u.value] = u.label; });
    return map;
  }, [userOptions]);

  const data = useMemo(() => {
    return allData.filter((bill) => {
      if (filter.ingredientVATName && !bill.ingredientVATName?.toLowerCase().includes(filter.ingredientVATName.toLowerCase())) return false;
      if (filter.startShift && bill.startShift !== filter.startShift) return false;
      if (filter.userId && String(bill.userId) !== filter.userId) return false;
      if (filter.status?.length && !filter.status.includes(getBillStatus(bill))) return false;
      return true;
    });
  }, [allData, filter]);

  const colTable = useMemo(() => [
    {
      id: "startDate",
      Header: "Ngày",
      accessor: (item) => item.startDate ? moment(item.startDate).format("DD/MM/YYYY") : "—",
    },
    {
      id: "startShift",
      Header: "Buổi",
      accessor: (item) => SHIFT_LABEL[item.startShift] || item.startShift || "—",
    },
    {
      id: "ingredientVATName",
      Header: "Hàng hoá",
      accessor: (item) => item.ingredientVATName || "—",
    },
    {
      id: "customerDirection",
      Header: "Loại",
      accessor: (item) => BILL_TYPE[item.customerDirection] || item.customerDirection || "—",
    },
    {
      id: "weight",
      Header: "KL (kg)",
      className: "text-right",
      accessor: (item) => item.weight ? item.weight.toLocaleString("en-US") : "—",
    },
    {
      id: "trips",
      Header: "Số chuyến",
      className: "text-center",
      accessor: (item) => Array.isArray(item.trips) ? item.trips.length : 0,
    },
    {
      id: "customerId",
      Header: "Công nợ",
      accessor: (item) => customerMap[item.customerId] || item.customerId || "—",
    },
    {
      id: "fromLocation",
      Header: "Điểm A",
      accessor: (item) => locationNameMap[item.fromLocation] || item.fromLocation || "—",
    },
    {
      id: "toLocation",
      Header: "Điểm B",
      accessor: (item) => locationNameMap[item.toLocation] || item.toLocation || "—",
    },
    {
      id: "userId",
      Header: "Người điều hành",
      accessor: (item) => userMap[String(item.userId)] || (item.userId ? `#${item.userId}` : "—"),
    },
    {
      id: "status",
      Header: "Trạng thái",
      accessor: (item) => <StatusBadge status={getBillStatus(item)} />,
    },
    {
      id: "rejectReason",
      Header: "Lý do từ chối",
      accessor: (item) => item.rejectReason
        ? <small className="text-danger">{item.rejectReason}</small>
        : "—",
    },
    {
      id: "actions",
      Header: "",
      accessor: (item) => (
        <Button
          size="sm"
          color="primary"
          outline
          onClick={(e) => { e.stopPropagation(); history.push(`/pn-request-bill/${item.id}`); }}
        >
          <Icon name="edit" />
        </Button>
      ),
    },
  ], [locationNameMap, customerMap, userMap, history]);

  if (!canAccess) return null;

  return (
    <React.Fragment>
      <Head title="Danh sách phiếu điều xe" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Danh sách phiếu điều xe</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => { ev.preventDefault(); setSmOption(!smOption); }}
                >
                  <Icon name="more-v" />
                </a>
                <div className="toggle-expand-content" style={{ display: smOption ? "block" : "none" }}>
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <Button color="primary" onClick={() => setCreateModal(true)}>
                        <Icon name="plus" />
                        <span>Tạo phiếu</span>
                      </Button>
                    </li>
                    <li>
                      <div className="form-control-wrap" style={{ minWidth: 180 }}>
                        <input
                          className="form-control"
                          placeholder="Tìm hàng hoá..."
                          value={filter.ingredientVATName}
                          onChange={(e) => setFilter({ ...filter, ingredientVATName: e.target.value })}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-control-wrap" style={{ minWidth: 140 }}>
                        <RSelect
                          isClearable
                          placeholder="Buổi"
                          options={SHIFT_OPTIONS}
                          value={SHIFT_OPTIONS.find((o) => o.value === filter.startShift) || null}
                          onChange={(val) => setFilter({ ...filter, startShift: val?.value || "" })}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-control-wrap" style={{ minWidth: 180 }}>
                        <RSelect
                          isClearable
                          placeholder="Người điều hành"
                          options={userOptions}
                          value={userOptions.find((o) => o.value === filter.userId) || null}
                          onChange={(val) => setFilter({ ...filter, userId: val?.value || "" })}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-control-wrap" style={{ minWidth: 200 }}>
                        <RSelect
                          isMulti
                          isClearable
                          placeholder="Trạng thái"
                          options={STATUS_OPTIONS}
                          value={STATUS_OPTIONS.filter((o) => filter.status?.includes(o.value))}
                          onChange={(vals) => setFilter({ ...filter, status: (vals || []).map((v) => v.value) })}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="form-control-wrap">
                        <DatePicker
                          selected={rangeDate.fromDate}
                          startDate={rangeDate.fromDate}
                          onChange={onRangeChange}
                          endDate={rangeDate.toDate}
                          selectsRange
                          className="form-control date-picker"
                        />
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          {/* Desktop: table */}
          <div className="d-none d-md-block card card-bordered card-preview">
            <div className="card-inner">
              <Table
                notShowPagi
                loading={loading}
                columns={colTable}
                data={data}
              />
            </div>
          </div>

          {/* Mobile: cards */}
          <div className="d-block d-md-none">
            {loading ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#8094ae" }}>Đang tải...</div>
            ) : data.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#8094ae" }}>Không có dữ liệu</div>
            ) : (
              <div style={{ padding: "0 2px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#8094ae", marginBottom: 6 }}>
                  <span>{data.length} phiếu</span>
                  <span style={{ fontStyle: "italic" }}>Chạm 2 lần để sửa</span>
                </div>
                {data.map((bill) => (
                  <BillCard
                    key={bill.id}
                    bill={bill}
                    locationNameMap={locationNameMap}
                    onEdit={(id) => history.push(`/pn-request-bill/${id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </Block>
      </Content>
      <CreatePNRequestBillModal
        isOpen={createModal}
        toggle={() => setCreateModal(false)}
        onCreated={(bill) => history.push(`/pn-request-bill/${bill.id}`)}
        pnCustomers={pnCustomers}
        locationDistances={locationDistances}
      />
    </React.Fragment>
  );
};

export default PNRequestBillManager;
